import { useState, useEffect, useRef, useCallback } from "react";
import { getContactPreview, getContactStatus } from "../../../../../../../services/contacts";
import { getNoteSnapshot } from "../../../../../../../services/notes";
import { listActivitiesAll } from "../../../../../../../services/activity";
import { getAppointments } from "../../../../../../../services/appointments";
import { STATUS_TAG_DICTIONARY } from "../../constants";

const BATCH_SIZE = 5;
const TAG_LABELS = Object.keys(STATUS_TAG_DICTIONARY);

// Deterministic hash from partyUuid — same contact always gets same mock values
const uuidHash = (uuid) =>
  (uuid || "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);

const seededPick = (arr, hash, offset = 0) =>
  arr[Math.abs(hash + offset) % arr.length];

// Mock fallback data pools — varied per contact via uuidHash
const MOCK_OWNERS = [
  "Sarah Johnson", "David Kim", "Marcus Chen", "Lisa Park",
  "Tom Rivera", "Ana Gutierrez", "James Wilson", "Priya Patel",
];
const MOCK_RELATIVE_TIMES = [
  "8m ago", "34m ago", "1h ago", "2h ago", "5h ago",
  "1d ago", "3d ago", "6d ago",
];
const MOCK_EMAIL_COUNTS = ["1", "2", "3", "4", "5"];
const MOCK_DATES = [
  "Jan 12", "Jan 28", "Feb 3", "Feb 15",
  "Mar 4", "Mar 21", "Apr 7", "Apr 19",
];
const MOCK_COMPANIES = [
  "Thornfield Auto Group", "Castillo & Sons Repair", "Westlake Service Center",
  "Bayside Collision Works", "Ridgeline Fleet Services", "Harmon Automotive",
  "Veltri Motor Group", "Clearwater Tire & Lube",
];
const MOCK_ROLES = ["Customer", "Employee", "Company"];
const MOCK_NBA_TEXTS = [
  "Schedule a follow-up call to discuss the pending service estimate and confirm next steps.",
  "Send a reminder about the upcoming appointment scheduled for this week.",
  "Reach out to confirm satisfaction with the recent repair and offer a maintenance package.",
  "Follow up on the open quote from last week before it expires.",
  "Escalated complaint about service delay - manager callback promised within 2 hours.",
  "Customer has not responded to the last two messages. Try a different channel.",
  "Warranty claim still pending review. Escalate to service manager if not resolved by end of day.",
  "Consider following up on the service delay complaint submitted. Reach out to the manager who promised a response within two business days and check if there are any escalations or follow-up tasks assigned to this case.",
];

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

const getRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const PARTY_TYPE_LABEL_MAP = {
  VIRTUAL: 'Customer',
  SECONDARY: 'Customer',
  ADVISOR: 'Company',
  HOUSEHOLD: 'Household',
};

const extractPreviewData = (response) => {
  const body = response?.body ?? response;
  if (!body) return { company: null, role: null, displayName: null };
  const rawPartyType = body.partyType || body.party_type || body.role || null;
  const role = PARTY_TYPE_LABEL_MAP[rawPartyType] ?? rawPartyType ?? null;
  return {
    company: body.company || body.companyName || body.company_name || null,
    displayName: body.displayName || body.display_name || null,
    role,
  };
};

const extractNbaFromSnapshot = (response) => {
  const body = response?.body;
  if (!Array.isArray(body)) return { nextBestAction: null, snapshotTags: [] };

  let nextBestAction = null;
  const snapshotTags = [];

  for (const group of body) {
    if (group.note_type === "NBA" && group.notes?.length > 0) {
      nextBestAction = group.notes[0].note_content || null;
    }
    if (group.note_type === "TAG") {
      for (const note of group.notes || []) {
        const category = note.note_content;
        if (category) {
          snapshotTags.push({ label: category, variant: "gray" });
        }
      }
    }
  }

  return { nextBestAction, snapshotTags };
};

const extractActivitySummary = (response) => {
  const body = response?.body;
  if (!body) return [];

  const emailCount = Array.isArray(body.email) ? body.email.length : 0;
  const smsCount = Array.isArray(body.sms) ? body.sms.length : 0;
  const noteCount = Array.isArray(body.note) ? body.note.length : 0;

  const allItems = [
    ...(body.email || []).map((e) => ({
      date: e.last_update || e.created_at,
      owner: e.sender,
    })),
    ...(body.sms || []).map((s) => ({
      date: s.last_update || s.created_at,
      owner: s.from,
    })),
    ...(body.note || []).map((n) => ({
      date: n.lastUpdate || n.last_update || n.createdAt || n.created_at,
      owner: n.ownerFullName || n.owner_full_name,
    })),
  ];

  allItems.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const items = [];
  const latestItem = allItems[0];

  if (latestItem?.owner) {
    items.push({ text: latestItem.owner });
  }

  if (latestItem?.date) {
    const relative = getRelativeTime(latestItem.date);
    if (relative) items.push({ icon: "schedule", text: relative });
  }

  if (emailCount > 0) {
    items.push({ icon: "email", text: String(emailCount) });
  }

  if (noteCount > 0) {
    items.push({ icon: "description", text: String(noteCount) });
  }

  if (smsCount > 0) {
    items.push({ icon: "sms", text: String(smsCount) });
  }

  const latestDate = latestItem?.date ? formatShortDate(latestItem.date) : null;
  if (latestDate) {
    items.push({ icon: "calendarToday", text: latestDate });
  }

  return items;
};

const SEVERITY_VARIANT_MAP = {
  INFORMATIVE: 'blue',
  ACTION: 'orange',
  URGENT: 'red',
};

const extractStatusTags = (response) => {
  const body = response?.body ?? response;
  if (!Array.isArray(body) || body.length === 0) return [];
  return body.map((alert) => ({
    label: alert.tagName,
    variant: SEVERITY_VARIANT_MAP[alert.severity] ?? 'blue',
  }));
};

const extractAppointmentTags = (response) => {
  const body = response?.body;
  if (!Array.isArray(body) || body.length === 0) return [];

  const tags = [];
  const hasAppointmentToday = body.some((appt) => {
    const date =
      appt.appointmentDate || appt.appointment_date || appt.timestamp;
    return isToday(date);
  });

  if (hasAppointmentToday) {
    tags.push({ label: "Appointment Today", variant: "blue" });
  }

  return tags;
};

const enrichSingleContact = async (partyUuid) => {
  const [previewResult, snapshotResult, activityResult, appointmentsResult, statusResult] =
    await Promise.allSettled([
      getContactPreview(partyUuid),
      getNoteSnapshot(partyUuid),
      listActivitiesAll({ party_uuid: partyUuid, page: 1, page_size: 5 }),
      getAppointments({ party_uuid: partyUuid, page: 1, page_size: 5 }),
      getContactStatus(partyUuid),
    ]);

  const preview =
    previewResult.status === "fulfilled"
      ? extractPreviewData(previewResult.value)
      : { company: null, displayName: null, role: null };

  const snapshot =
    snapshotResult.status === "fulfilled"
      ? extractNbaFromSnapshot(snapshotResult.value)
      : { nextBestAction: null, snapshotTags: [] };

  const activitySummary =
    activityResult.status === "fulfilled"
      ? extractActivitySummary(activityResult.value)
      : [];

  const appointmentTags =
    appointmentsResult.status === "fulfilled"
      ? extractAppointmentTags(appointmentsResult.value)
      : [];

  const alertTags =
    statusResult.status === "fulfilled"
      ? extractStatusTags(statusResult.value)
      : [];

  const statusTags = [...alertTags, ...appointmentTags, ...snapshot.snapshotTags];

  const hash = uuidHash(partyUuid);

  return {
    // TODO [MOCK-1]: company field pending stable DB column/relationship in backend
    company: preview.company || seededPick(MOCK_COMPANIES, hash),
    role: preview.role || seededPick(MOCK_ROLES, hash, 3),
    // TODO [MOCK-3]: real NBA from /note/snapshot when available; else varied fallback copy
    nextBestAction: snapshot.nextBestAction || seededPick(MOCK_NBA_TEXTS, hash, 7),
    activitySummary:
      activitySummary.length > 0
        ? activitySummary
        : [
            { text: seededPick(MOCK_OWNERS, hash) },
            { icon: "schedule", text: seededPick(MOCK_RELATIVE_TIMES, hash, 1) },
            { icon: "email", text: seededPick(MOCK_EMAIL_COUNTS, hash, 2) },
            { icon: "calendarToday", text: seededPick(MOCK_DATES, hash, 5) },
          ],
    statusTags,
  };
};

const processBatch = async (uuids, onResult, abortRef) => {
  for (let i = 0; i < uuids.length; i += BATCH_SIZE) {
    if (abortRef.current) return;

    const batch = uuids.slice(i, i + BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map((uuid) => enrichSingleContact(uuid)),
    );

    if (abortRef.current) return;

    const batchResults = {};
    batch.forEach((uuid, idx) => {
      if (results[idx].status === "fulfilled") {
        batchResults[uuid] = results[idx].value;
      }
    });

    onResult(batchResults);
  }
};

export const useContactEnrichment = (contacts) => {
  const [enrichments, setEnrichments] = useState({});
  const [loadingEnrichment, setLoadingEnrichment] = useState(false);
  const abortRef = useRef(false);
  const inFlightRef = useRef(new Set());
  const cacheRef = useRef({});

  const handleBatchResult = useCallback((batchResults) => {
    Object.assign(cacheRef.current, batchResults);
    setEnrichments((prev) => ({ ...prev, ...batchResults }));
  }, []);

  useEffect(() => {
    if (!contacts || contacts.length === 0) {
      setEnrichments({});
      setLoadingEnrichment(false);
      return;
    }

    const uuidsToEnrich = [];
    const cached = {};

    for (const contact of contacts) {
      const uuid = contact.partyUuid;
      if (!uuid) continue;

      if (cacheRef.current[uuid]) {
        cached[uuid] = cacheRef.current[uuid];
      } else if (!inFlightRef.current.has(uuid)) {
        uuidsToEnrich.push(uuid);
      }
    }

    if (Object.keys(cached).length > 0) {
      setEnrichments((prev) => ({ ...prev, ...cached }));
    }

    if (uuidsToEnrich.length === 0) {
      setLoadingEnrichment(false);
      return;
    }

    abortRef.current = false;
    setLoadingEnrichment(true);
    uuidsToEnrich.forEach((uuid) => inFlightRef.current.add(uuid));

    processBatch(uuidsToEnrich, handleBatchResult, abortRef).finally(() => {
      uuidsToEnrich.forEach((uuid) => inFlightRef.current.delete(uuid));
      setLoadingEnrichment(false);
    });

    return () => {
      abortRef.current = true;
    };
  }, [contacts, handleBatchResult]);

  return { enrichments, loadingEnrichment };
};
